// The "Block Library" plan tab: every library entry of the team as a card, with a search box, the
// All / Created by you pills, and "New Block". The ⋮ menu of a card has Edit, Duplicate, and Delete for the
// entries of the user, and Duplicate for the others (also for an entry of Whistle Performance,
// which has no owner and is never in "Created by you"). The plan page can open a card from another
// tab (`focusId`): the tab clears the filters, scrolls the card into view, and marks it for a
// short time.
import { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Empty, Input, Modal, Skeleton, Tag } from "antd";
import type { MenuProps } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import {
	blockEntryMatches,
	blockEntryUsageChips,
	getBlockEntryCreator,
	getInitials,
	getRtpErrorMessage,
	nextCopyTitle,
} from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useCurrentUserUuid, useRtpMetricOptions } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BlockContentView } from "./BlockContentView";
import { BlockLibraryEntryEditor } from "./BlockLibraryEntryEditor";
import type { RtpBlockTemplate, RtpPlanDetail } from "./types";

const FLASH_MS = 1500;

const MENU_EDIT = {
	key: "edit",
	icon: <EditOutlined />,
	label: <span data-attr="settings.rtp-block-library.card-edit">Edit</span>,
};
const MENU_DUPLICATE = {
	key: "duplicate",
	icon: <CopyOutlined />,
	label: <span data-attr="settings.rtp-block-library.card-duplicate">Duplicate</span>,
};
const MENU_DELETE = {
	key: "delete",
	icon: <DeleteOutlined />,
	danger: true,
	label: <span data-attr="settings.rtp-block-library.card-delete">Delete</span>,
};
const OWN_ENTRY_MENU: MenuProps["items"] = [MENU_EDIT, MENU_DUPLICATE, MENU_DELETE];
const OTHER_ENTRY_MENU: MenuProps["items"] = [MENU_DUPLICATE];

interface BlockLibraryCardProps {
	entry: RtpBlockTemplate;
	isYou: boolean;
	flash: boolean;
	// The ⋮ menu is hidden for a user who cannot create blocks.
	canCreate: boolean;
	metricLabel: (key: string) => string;
	onMenu: (key: string, entry: RtpBlockTemplate) => void;
}

const BlockLibraryCard = ({ entry, isYou, flash, canCreate, metricLabel, onMenu }: BlockLibraryCardProps) => {
	const creator = getBlockEntryCreator(entry);
	const chips = blockEntryUsageChips(entry);
	return (
		<div className={`rtp-bl-card${flash ? " rtp-bl-card--flash" : ""}`} data-entry-id={entry.id}>
			<div className="rtp-bl-head">
				<div className="rtp-bl-title">{entry.title}</div>
				{canCreate ? (
					<Dropdown
						trigger={["click"]}
						placement="bottomRight"
						menu={{
							items: entry.canEdit ? OWN_ENTRY_MENU : OTHER_ENTRY_MENU,
							onClick: ({ key }) => onMenu(key, entry),
						}}
					>
						<Button
							className="rtp-bl-menu"
							type="text"
							size="small"
							icon={<MoreOutlined />}
							aria-label="Block options"
							data-attr="settings.rtp-block-library.card-menu"
						/>
					</Dropdown>
				) : null}
			</div>
			<div className="rtp-bl-author">
				<span className={`rtp-bl-av${entry.source === "whistle" ? " rtp-bl-av--whistle" : ""}`}>
					{entry.source === "whistle" ? "W" : getInitials(creator)}
				</span>
				{creator}
				{isYou ? " (you)" : ""}
				{entry.source === "whistle" ? "" : ` · ${dayjs(entry.createdAt).format("MMM D")}`}
			</div>
			<div className="rtp-bl-body">
				<BlockContentView
					content={entry.content}
					metricLabel={metricLabel}
					collapsible
				/>
			</div>
			{chips.length > 0 ? (
				<div className="rtp-bl-chips">
					{chips.map((chip) => (
						<span key={chip} className="rtp-bl-chip">
							{chip}
						</span>
					))}
				</div>
			) : null}
		</div>
	);
};

interface BlockLibraryTabProps {
	// The player of the plan feeds the stats hint of the target rows in the editor.
	plan: RtpPlanDetail;
	library: RtpBlockLibraryState;
	// The id of the entry to bring into view, or null. The tab calls `onFocusHandled` when it has it.
	focusId: string | null;
	onFocusHandled: () => void;
}

export const BlockLibraryTab = ({ plan, library, focusId, onFocusHandled }: BlockLibraryTabProps) => {
	const currentUserUuid = useCurrentUserUuid();
	const { byKey } = useRtpMetricOptions();
	const metricLabel = (key: string) => byKey[key]?.label ?? key;

	const [query, setQuery] = useState("");
	const [mine, setMine] = useState(false);
	const [flashId, setFlashId] = useState<string | null>(null);
	// `entry` null opens the editor for a new entry.
	const [editor, setEditor] = useState<{ entry: RtpBlockTemplate | null } | null>(null);
	const gridRef = useRef<HTMLDivElement>(null);

	// A filter could hide the card, so the first step clears them. The scroll waits for the next
	// render, which shows the card.
	useEffect(() => {
		if (!focusId) return;
		setQuery("");
		setMine(false);
		setFlashId(focusId);
		onFocusHandled();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusId]);

	useEffect(() => {
		if (!flashId) return;
		const card = gridRef.current?.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(flashId)}"]`);
		card?.scrollIntoView({ block: "center", behavior: "smooth" });
		const timer = setTimeout(() => setFlashId(null), FLASH_MS);
		return () => clearTimeout(timer);
	}, [flashId]);

	const isOwn = (entry: RtpBlockTemplate) => Boolean(currentUserUuid) && entry.createdBy?.uuid === currentUserUuid;
	const rows = library.templates.filter(
		(entry) => (!mine || isOwn(entry)) && blockEntryMatches(entry, query, metricLabel)
	);

	const duplicate = async (entry: RtpBlockTemplate) => {
		try {
			const copy: RtpBlockTemplate = await rtpApi.createRtpBlockTemplate({
				title: nextCopyTitle(entry.title, library.templates.map((item) => item.title)),
				content: entry.content,
			});
			await library.reload();
			setEditor({ entry: copy });
		} catch (e) {
			message.error(getRtpErrorMessage(e));
		}
	};

	const confirmDelete = (entry: RtpBlockTemplate) => {
		Modal.confirm({
			title: "Delete this library block?",
			content: "Blocks already on a schedule keep their content.",
			okText: "Delete",
			okType: "danger",
			okButtonProps: { "data-attr": "settings.rtp-block-library.delete-submit" } as any,
			cancelButtonProps: { "data-attr": "settings.rtp-block-library.delete-cancel" } as any,
			onOk: async () => {
				if (!(await runRtpWrite(() => rtpApi.deleteRtpBlockTemplate(entry.id)))) return;
				await library.reload();
				message.success("Library block deleted.");
			},
		});
	};

	const menuActions: Record<string, (entry: RtpBlockTemplate) => void> = {
		edit: (entry) => setEditor({ entry }),
		duplicate,
		delete: confirmDelete,
	};

	const emptyText = library.templates.length === 0 ? "The block library is empty." : "No blocks match your search.";

	return (
		<div>
			<div className="rtp-bl-tools">
				<Input
					className="rtp-bl-search"
					allowClear
					placeholder="Search blocks"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
				/>
				<div className="rtp-pills rtp-coach-filter">
					<Tag.CheckableTag
						checked={!mine}
						data-attr="settings.rtp-block-library.filter-all"
						onChange={() => setMine(false)}
					>
						All
					</Tag.CheckableTag>
					<Tag.CheckableTag
						checked={mine}
						data-attr="settings.rtp-block-library.filter-mine"
						onChange={() => setMine(true)}
					>
						Created by you
					</Tag.CheckableTag>
				</div>
				{library.canCreate ? (
					<Button
						className="rtp-bl-new"
						type="primary"
						icon={<PlusOutlined />}
						data-attr="settings.rtp-block-library.new"
						onClick={() => setEditor({ entry: null })}
					>
						New Block
					</Button>
				) : null}
			</div>

			{library.loading && library.templates.length === 0 ? (
				<Skeleton active />
			) : (
				<div ref={gridRef} className="rtp-bl-grid">
					{rows.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} /> : null}
					{rows.map((entry) => (
						<BlockLibraryCard
							key={entry.id}
							entry={entry}
							isYou={isOwn(entry)}
							flash={flashId === entry.id}
							canCreate={library.canCreate}
							metricLabel={metricLabel}
							onMenu={(key, target) => menuActions[key]?.(target)}
						/>
					))}
				</div>
			)}

			{editor ? (
				<BlockLibraryEntryEditor
					key={editor.entry?.id ?? "new"}
					entry={editor.entry}
					library={library}
					player={{ id: plan.playerId, name: plan.playerName }}
					onClosed={() => setEditor(null)}
				/>
			) : null}
		</div>
	);
};
